import { useDraggable } from "@dnd-kit/react";
import { Book, Edit2, GripVertical, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useBundleTree, useBundleTreeActions } from "../bundleTreeStore";
import { GroupType } from "../bundleTypes";
import { InlineTextEditor } from "../InlineTextEditor";
import { RowActions } from "../RowActions";
import { useScrollIntoViewWhen } from "../useScrollIntoViewWhen";

type EditVolumeProps = {
  groupId: string;
  scrollTo?: boolean;
  onScrollComplete?: () => void;
};

export const EditVolume = ({ groupId, scrollTo, onScrollComplete }: EditVolumeProps) => {
  const config = useBundleTree((s) => s.config);
  const { updateGroup: onUpdateGroup, removeGroup: onRemoveGroup } = useBundleTreeActions();
  const group = config.groups[groupId];

  const { ref: sortRef, handleRef, isDragSource } = useDraggable({
    id: groupId,
    data: { type: 'group', groupId },
  });

  const [editingTitle, setEditingTitle] = useState(false);
  const elRef = useRef<HTMLDivElement | null>(null);

  const clearIsNew = useCallback(() => onUpdateGroup(groupId, { isNew: false }), [onUpdateGroup, groupId]);
  useScrollIntoViewWhen(elRef, group.isNew, clearIsNew);
  useScrollIntoViewWhen(elRef, scrollTo, onScrollComplete);

  const isDuplicateVolumeName = (name: string) => {
    const lower = name.toLowerCase();
    return Object.values(config.groups).some(
      (g) => g.groupId !== groupId && g.groupType === GroupType.VOLUME && g.title.toLowerCase() === lower
    );
  };

  const handleSubmitTitle = (text: string) => {
    const finalTitle = text.trim() || group.title;
    if (isDuplicateVolumeName(finalTitle)) {
      toast.error(`A volume named "${finalTitle}" already exists`);
      return;
    }
    onUpdateGroup(groupId, { title: finalTitle, isNew: false });
    setEditingTitle(false);
  };

  return (
    <div
      ref={(node) => { sortRef(node); elRef.current = node; }}
      className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50/60 border-blue-200 transition-colors group/volume ${isDragSource ? 'opacity-30' : ''}`}
    >
      <GripVertical ref={handleRef} className="h-4 w-4 text-blue-300 shrink-0 cursor-grab outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:rounded-sm" />
      <Book className="h-4 w-4 text-blue-500 shrink-0" />

      {editingTitle ? (
        <InlineTextEditor
          initialValue={group.title}
          onSubmit={handleSubmitTitle}
          onCancel={() => setEditingTitle(false)}
          placeholder="Volume name..."
          inputClassName="h-7 text-sm flex-1"
        />
      ) : (
        <>
          <span
            className="text-sm font-semibold text-blue-700 flex-1 cursor-pointer"
            onDoubleClick={() => setEditingTitle(true)}
          >
            {group.title}
          </span>
          <RowActions
            hoverGroup="group-hover/volume"
            tone="blue"
            actions={[
              { icon: Edit2, label: 'Rename volume', onClick: () => setEditingTitle(true) },
              { icon: Trash2, label: 'Delete volume', onClick: () => onRemoveGroup(groupId), danger: true },
            ]}
          />
        </>
      )}
    </div>
  );
};
